<template>
  <div class="app-container openapi-key">
    <el-card class="box-card">
      <div class="page-title">API Key 管理</div>
      <div class="btn-box mb10">
        <el-button type="primary" size="mini" icon="el-icon-plus" @click="handleCreate"
                   v-hasPermi="['openapi:key:edit']">新建 Key</el-button>
      </div>
      <div class="tip-box mb10">
        <i class="el-icon-warning-outline"></i>
        Key 明文<b>只在新建成功那一次显示</b>，关掉弹窗后系统里再也查不到（库里只存 SHA-256）。
        忘了就只能停用旧的、重新建一把。
      </div>

      <el-table v-loading="loading" :data="list">
        <el-table-column label="接入方名称" prop="appName" align="center" width="170" />
        <el-table-column label="Key 前缀" prop="keyPrefix" align="center" width="110">
          <template slot-scope="{row}">
            <!-- 只有前 8 位，用来在列表里认出是哪一把；明文与 hash 都不下发到前端 -->
            <span class="mono">{{ row.keyPrefix }}…</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" align="center" width="90">
          <template slot-scope="{row}">
            <el-tag size="mini" :type="+row.enabled === 1 ? 'success' : 'info'">
              {{ +row.enabled === 1 ? '启用' : '已停用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="授权接口" min-width="200"><template slot-scope="{row}">{{ (row.apiCodes || []).join(', ') || '未授权' }}</template></el-table-column>
        <el-table-column label="IP 白名单" prop="ipWhitelist" align="center" min-width="180" show-overflow-tooltip>
          <template slot-scope="{row}">
            <span v-if="row.ipWhitelist">{{ row.ipWhitelist }}</span>
            <span v-else class="text-warn">不限 IP</span>
          </template>
        </el-table-column>
        <el-table-column label="备注" prop="remark" align="center" min-width="160" show-overflow-tooltip>
          <template slot-scope="{row}">{{ row.remark || '—' }}</template>
        </el-table-column>
        <el-table-column label="最后调用" align="center" width="170">
          <template slot-scope="{row}">
            <span v-if="!row.lastUsedAt" class="text-warn">从未调用</span>
            <span v-else :class="{ 'text-warn': isStale(row.lastUsedAt) }">
              {{ row.lastUsedAt }}
              <!-- 超过 IDLE_DAYS 天没调用的标出来：长期不用的 Key 就该停掉，这是安全面 -->
              <el-tag v-if="isStale(row.lastUsedAt)" size="mini" type="warning">闲置</el-tag>
            </span>
          </template>
        </el-table-column>
        <el-table-column label="创建人" prop="createdBy" align="center" width="100" />
        <el-table-column label="创建时间" prop="createdTime" align="center" width="170" />
        <el-table-column label="操作" align="center" width="150" fixed="right">
          <template slot-scope="{row}">
            <el-button type="text" @click="handleUpdate(row)"
                       v-hasPermi="['openapi:key:edit']">编辑</el-button>
            <el-button type="text" :disabled="+row.enabled !== 1" @click="handleDisable(row)"
                       v-hasPermi="['openapi:key:disable']">停用</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 新建 -->
    <el-dialog title="新建 API Key" :visible.sync="createVisible" width="520px" :close-on-click-modal="false">
      <el-form ref="createForm" :model="createForm" :rules="createRules" label-width="110px">
        <el-form-item label="接入方名称" prop="appName">
          <el-input v-model="createForm.appName" maxlength="64" show-word-limit placeholder="例如：财务对账" />
        </el-form-item>
        <el-form-item label="IP 白名单">
          <el-input v-model="createForm.ipWhitelist" maxlength="500" type="textarea" :rows="2"
                    placeholder="多个用英文逗号分隔；留空表示不限 IP" />
          <div class="form-tip">留空 = 不限 IP。只支持精确 IP，不支持网段。</div>
        </el-form-item>
        <el-form-item label="授权接口">
          <el-select v-model="createForm.apiCodes" multiple :loading="catalogLoading" placeholder="不选择即不授权任何接口" style="width: 100%">
            <el-option v-for="api in catalogOptions" :key="api.code" :value="api.code"
                       :label="api.method + ' ' + api.name" :disabled="api.state !== 'OK'" />
          </el-select>
          <div class="form-tip">仅可调用选中接口；清空并保存会立即撤销全部授权。</div>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="createForm.remark" maxlength="255" placeholder="对接人、用途等" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="createVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="submitting" @click="submitCreate">确定</el-button>
      </div>
    </el-dialog>

    <!--
      明文展示弹窗。
      ⚠️ 只能在 create 的响应里拿到一次，所以这个弹窗**不允许点外面关闭**，
         也不放"以后再看"这种按钮 —— 没有"以后"。
    -->
    <el-dialog title="Key 创建成功" :visible.sync="plainVisible" width="620px"
               :close-on-click-modal="false" :close-on-press-escape="false" :show-close="false">
      <el-alert type="warning" :closable="false" show-icon
                title="这是唯一一次显示明文，关闭后无法再查看，请立即复制并交给接入方。" />
      <div class="plain-key mt10">
        <el-input :value="plainKey" readonly class="mono" />
        <el-button type="primary" size="small" class="ml10" @click="copyPlainKey">复制</el-button>
      </div>
      <div class="form-tip mt10">接入方调用时放在请求头 <code>X-API-Key</code> 里。</div>
      <div slot="footer">
        <el-checkbox v-model="plainCopied">我已复制并妥善保存</el-checkbox>
        <el-button size="small" type="primary" :disabled="!plainCopied" class="ml10"
                   @click="closePlain">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 编辑：授权集与 IP 白名单、备注同一事务保存 -->
    <el-dialog title="编辑 API Key" :visible.sync="editVisible" width="520px" :close-on-click-modal="false">
      <el-form ref="editForm" :model="editForm" label-width="110px">
        <el-form-item label="接入方名称">
          <!-- 名称不给改：访问日志里的 app_name 是调用当时写进去的，改名会让历史日志裂成两个接入方 -->
          <el-input :value="editForm.appName" disabled />
          <div class="form-tip">名称不可修改（改名会让历史调用日志对不上）。要换名请停用后重建。</div>
        </el-form-item>
        <el-form-item label="IP 白名单">
          <el-input v-model="editForm.ipWhitelist" maxlength="500" type="textarea" :rows="2"
                    placeholder="多个用英文逗号分隔；留空表示不限 IP" />
          <div class="form-tip">
            <b>留空 = 清空 = 不限 IP</b>，不是"保持原值"。保存后立即生效。
          </div>
        </el-form-item>
        <el-form-item label="授权接口">
          <el-select v-model="editForm.apiCodes" multiple :loading="catalogLoading" placeholder="不选择即不授权任何接口" style="width: 100%">
            <el-option v-for="api in catalogOptions" :key="api.code" :value="api.code"
                       :label="api.method + ' ' + api.name" :disabled="api.state !== 'OK'" />
          </el-select>
          <div class="form-tip">仅可调用选中接口；清空并保存会立即撤销全部授权。</div>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="editForm.remark" maxlength="255" placeholder="对接人、用途等" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="editVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="submitting" @click="submitUpdate">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import openApiManageApi from '@/api/openapi'

/** 超过这么多天没调用就标「闲置」 */
const IDLE_DAYS = 30

export default {
  name: 'OpenapiKey',
  data() {
    return {
      list: [],
      catalogOptions: [],
      catalogLoading: true,
      loading: false,
      submitting: false,
      createVisible: false,
      createForm: { appName: '', ipWhitelist: '', remark: '', apiCodes: [] },
      createRules: {
        appName: [{ required: true, message: '请填写接入方名称', trigger: 'blur' }]
      },
      plainVisible: false,
      plainKey: '',
      plainCopied: false,
      editVisible: false,
      editForm: { id: undefined, appName: '', ipWhitelist: '', remark: '', apiCodes: [] }
    }
  },
  created() {
    this.loadCatalog()
    this.doQueryList()
  },
  methods: {
    loadCatalog() {
      this.catalogLoading = true
      return openApiManageApi.catalog().then(data => {
        this.catalogOptions = ((data && data.data) || []).filter(api => api.code)
      }).finally(() => { this.catalogLoading = false })
    },
    doQueryList() {
      this.loading = true
      return openApiManageApi.keyList()
        .then(data => {
          this.list = (data && data.data) || []
        })
        .finally(() => {
          this.loading = false
        })
    },
    isStale(lastUsedAt) {
      if (!lastUsedAt) return true
      // 后端给的是 'yyyy-MM-dd HH:mm:ss'，Safari 对带横杠的格式解析不稳，换成斜杠再解析
      const t = new Date(String(lastUsedAt).replace(/-/g, '/')).getTime()
      if (isNaN(t)) return false
      return Date.now() - t > IDLE_DAYS * 24 * 3600 * 1000
    },
    handleCreate() {
      this.createForm = { appName: '', ipWhitelist: '', remark: '', apiCodes: [] }
      this.loadCatalog()
      this.createVisible = true
      this.$nextTick(() => this.$refs.createForm && this.$refs.createForm.clearValidate())
    },
    submitCreate() {
      this.$refs.createForm.validate(valid => {
        if (!valid) return
        this.submitting = true
        openApiManageApi.keyCreate(this.createForm)
          .then(data => {
            const vo = (data && data.data) || {}
            this.createVisible = false
            // 明文只有这一次，先把弹窗立起来再刷新列表，顺序反了会让人以为没创建成功
            this.plainKey = vo.apiKey || ''
            this.plainCopied = false
            this.plainVisible = true
            return this.doQueryList()
          })
          .finally(() => {
            this.submitting = false
          })
      })
    },
    copyPlainKey() {
      const input = document.createElement('textarea')
      input.value = this.plainKey
      document.body.appendChild(input)
      input.select()
      try {
        document.execCommand('copy')
        this.$message.success('已复制到剪贴板')
        this.plainCopied = true
      } catch (e) {
        this.$message.warning('复制失败，请手动选中复制')
      }
      document.body.removeChild(input)
    },
    closePlain() {
      this.plainVisible = false
      // 不把明文留在内存里
      this.plainKey = ''
    },
    handleUpdate(row) {
      this.editForm = {
        id: row.id,
        appName: row.appName,
        ipWhitelist: row.ipWhitelist || '',
        remark: row.remark || '',
        apiCodes: (row.apiCodes || []).slice()
      }
      this.loadCatalog()
      this.editVisible = true
    },
    submitUpdate() {
      this.submitting = true
      openApiManageApi.keyUpdate({
        id: this.editForm.id,
        ipWhitelist: this.editForm.ipWhitelist,
        remark: this.editForm.remark,
        apiCodes: this.editForm.apiCodes
      })
        .then(() => {
          this.$message.success('保存成功')
          this.editVisible = false
          return this.doQueryList()
        })
        .finally(() => {
          this.submitting = false
        })
    },
    handleDisable(row) {
      return this.$common.confirm(
        `停用后【${row.appName}】立即无法调用，且不可恢复，需要重新创建。确认停用？`)
        .then(() => openApiManageApi.keyDisable(row.id))
        .then(() => {
          this.$message.success('已停用')
          return this.doQueryList()
        })
        .catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
.openapi-key {
  .page-title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 12px;
  }
  .tip-box {
    font-size: 13px;
    color: #e6a23c;
    line-height: 20px;
  }
  .form-tip {
    font-size: 12px;
    color: #909399;
    line-height: 18px;
  }
  .text-warn {
    color: #e6a23c;
  }
  .mono {
    font-family: Consolas, Monaco, monospace;
  }
  .plain-key {
    display: flex;
    align-items: center;
  }
  .ml10 {
    margin-left: 10px;
  }
  .mt10 {
    margin-top: 10px;
  }
}
</style>
